import { useLocation, useNavigate } from "react-router-dom"
import {
  LayoutDashboard,
  Users,
  Calendar,
  MessageSquare,
  BookOpen,
  Image,
  Settings,
  LogOut,
  MonitorPlay,
  ScrollText,
  FlaskConical,
  UserCheck,
  Activity,
  ClipboardCheck,
  Building2,
  UserPlus,
  ShieldCheck,
  HeartPulse,
  Globe,
  Dumbbell,
  MessagesSquare,
  MessageSquareText,
  Route,
  CreditCard,
  Watch,
  Bot,
  Wrench,
  Inbox,
} from "lucide-react"
import { useAuth } from "@/lib/auth"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar"

// Revenue is founder-only. This list must stay in step with
// core.auth.REVENUE_ADMIN_EMAILS — the BACKEND is the actual gate (it 403s), so
// this only decides whether a colleague sees a link that would refuse them.
const REVENUE_EMAILS = [
  "quinn@offseasonwellness.com",
  "alyson@offseasonwellness.com",
]

const navMain = [
  { title: "Dashboard", icon: LayoutDashboard, url: "/" },
  { title: "iMessage", icon: MessageSquareText, url: "/imessage" },
  { title: "Onboarding paths", icon: Route, url: "/onboarding-paths" },
  { title: "Users", icon: Users, url: "/users" },
  { title: "Seasons", icon: Calendar, url: "/seasons" },
]

// Labelled for the question it answers, not the vendor behind it. The route
// stays /revenuecat so existing links keep working.
const navRevenue = { title: "Revenue", icon: CreditCard, url: "/revenuecat" }

const navContent = [
  { title: "Chat Edits", icon: MessageSquare, url: "/chat" },
  { title: "Chat Transcripts", icon: MessagesSquare, url: "/chat-transcripts" },
  { title: "Principles", icon: BookOpen, url: "/principles" },
  { title: "Prompts", icon: ScrollText, url: "/prompts" },
  { title: "Playground", icon: FlaskConical, url: "/playground" },
  { title: "Agent Sandbox", icon: Bot, url: "/agent-sandbox" },
  { title: "Media Library", icon: Image, url: "/media" },
]

const navSystem = [
  { title: "Users & Access", icon: UserCheck, url: "/waitlist" },
  { title: "Referrals", icon: UserPlus, url: "/referrals" },
  { title: "Studios", icon: Building2, url: "/studios" },
  { title: "Studio Health", icon: HeartPulse, url: "/studio-health" },
  { title: "Studio Information", icon: Globe, url: "/studio-info" },
  { title: "Class Catalog", icon: Dumbbell, url: "/class-catalog" },
  { title: "Booking Audit", icon: MonitorPlay, url: "/booking-audit" },
  { title: "Diagnosis Digest", icon: Activity, url: "/diagnosis" },
  { title: "Member Reports", icon: Inbox, url: "/member-reports" },
  { title: "Ops", icon: Wrench, url: "/ops" },
  { title: "Wearables", icon: Watch, url: "/wearables" },
  { title: "Program Integrity", icon: ShieldCheck, url: "/integrity" },
  { title: "Flow Audit", icon: ClipboardCheck, url: "/audit" },
  { title: "Settings", icon: Settings, url: "/settings" },
]

export function AppSidebar() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  // Case-insensitive: Supabase doesn't guarantee canonical casing on the email,
  // and a capitalised address shouldn't cost a founder their own dashboard.
  const canSeeRevenue = REVENUE_EMAILS.includes(
    (user?.email ?? "").trim().toLowerCase(),
  )
  const mainItems = canSeeRevenue ? [...navMain, navRevenue] : navMain

  function isActive(url: string) {
    if (url === "/") return location.pathname === "/"
    // Match on a path-segment boundary, not a bare prefix - otherwise /chat
    // also lights up for /chat-transcripts and two entries look selected.
    return location.pathname === url || location.pathname.startsWith(`${url}/`)
  }

  function renderGroup(label: string, items: typeof navMain) {
    return (
      <SidebarGroup>
        <SidebarGroupLabel>{label}</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            {items.map((item) => (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  isActive={isActive(item.url)}
                  onClick={() => navigate(item.url)}
                  tooltip={item.title}
                >
                  <item.icon className="size-4" />
                  <span>{item.title}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    )
  }

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              onClick={() => navigate("/")}
              className="font-bold"
            >
              <img
                src="/app-icon.png"
                alt="Offseason"
                className="aspect-square size-8 rounded-lg object-cover"
              />
              <span className="truncate text-lg font-bold tracking-tight">Offseason</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {renderGroup("Main", mainItems)}
        {renderGroup("Content", navContent)}
        {renderGroup("System", navSystem)}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg">
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-muted text-muted-foreground text-xs font-medium">
                {user?.email?.[0]?.toUpperCase() ?? "?"}
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {String(user?.user_metadata?.full_name || user?.user_metadata?.name || "Admin")}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {user?.email ?? "Not signed in"}
                </span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={signOut}
              tooltip="Sign out"
            >
              <LogOut className="size-4" />
              <span>Sign out</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  )
}
